import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import DailyWorkbook from './DailyWorkbook'
import Pipeline from './Pipeline'
import HotTen from './HotTen'
import MySearch from './MySearch'
import Progress from './Progress'
import { MilestonePopup, useMilestones } from './Celebrations'
import ResumeAnalyzer from './ResumeAnalyzer'
import Forecast from './Forecast'
import SearchJournal from './SearchJournal'
import CURRICULUM from '../data/curriculum'

const NAV = [
  { id: 'today', label: 'Today', icon: '📋' },
  { id: 'mysearch', label: 'My Search', icon: '🗂️' },
  { id: 'pipeline', label: 'Pipeline', icon: '🎯' },
  { id: 'network', label: 'Hot Ten', icon: '👥' },
  { id: 'resume', label: 'Resume', icon: '📄' },
]

export default function Dashboard({ session, profile, onSignOut, onProfileUpdate }) {
  const [activeTab, setActiveTab] = useState('today')
  const [subView, setSubView] = useState(null) // 'day1' | 'day2' | 'day8' | 'resume' | 'progress' | 'resources'
  const [stats, setStats] = useState({ pipeline: 0, hotTen: 0, referrals: 0, interviews: 0, activeDays: 0 })
  const [daysSinceActive, setDaysSinceActive] = useState(null)
  const [earnedMilestones, setEarnedMilestones] = useState([])
  const [showWelcome] = useState(() => {
    try { return !localStorage.getItem(`welcome_${session.user.id}`) } catch { return false }
  })

  // Day calculation — based on highest completed day + 1
  const [highestDay, setHighestDay] = useState(1)
  const currentDay = highestDay

  useEffect(() => {
    loadStats()
    loadMilestones()
  }, [])

  async function loadStats() {
    const [p, h, c] = await Promise.all([
      supabase.from('pipeline').select('id, stage, referral_status').eq('user_id', session.user.id),
      supabase.from('hot_ten').select('id, status').eq('user_id', session.user.id),
      supabase.from('daily_completions').select('completed_at, day_number').eq('user_id', session.user.id),
    ])
    const pipeline = p.data || []
    const hotTen = h.data || []
    const completions = c.data || []
    const uniqueDays = new Set(completions.map(d => d.completed_at?.split('T')[0]))

    // Calculate current day from highest completed day
    const completedDayNumbers = completions.map(d => d.day_number).filter(Boolean)
    const maxCompleted = completedDayNumbers.length > 0 ? Math.max(...completedDayNumbers) : 0
    const nextDay = Math.min(maxCompleted + 1, 88)
    setHighestDay(Math.max(nextDay, 1))

    // Gap detection — no shame, just a warm nudge if it's been a few days
    const sortedDates = [...uniqueDays].sort()
    const lastActive = sortedDates.length ? sortedDates[sortedDates.length - 1] : null
    const gapDays = lastActive ? Math.floor((new Date() - new Date(lastActive)) / (1000 * 60 * 60 * 24)) : 0
    setDaysSinceActive(lastActive ? gapDays : null)

    setStats({
      pipeline: pipeline.filter(i => i.stage !== 'rejected').length,
      hotTen: hotTen.filter(h => h.status === 'activated').length,
      referrals: pipeline.filter(i => i.referral_status === 'confirmed').length,
      interviews: pipeline.filter(i => ['interview', 'final_round', 'offer'].includes(i.stage)).length,
      activeDays: uniqueDays.size,
    })
  }

  async function loadMilestones() {
    const { data } = await supabase.from('badges').select('badge_id').eq('user_id', session.user.id)
    setEarnedMilestones((data || []).map(b => b.badge_id))
  }

  async function earnMilestone(id) {
    await supabase.from('badges').upsert({ user_id: session.user.id, badge_id: id, earned_at: new Date().toISOString() })
    setEarnedMilestones(prev => [...new Set([...prev, id])])
  }

  const { pendingMilestone, clearMilestone } = useMilestones(profile, stats, earnedMilestones, earnMilestone)

  async function handleDayComplete(day) {
    if (!day || day < 1) return
    try {
      // First try upsert with constraint
      const { error } = await supabase.from('daily_completions').upsert({
        user_id: session.user.id, 
        day_number: parseInt(day), 
        completed_at: new Date().toISOString(),
      }, { onConflict: 'user_id,day_number' })
      
      if (error) {
        console.error('Upsert error:', error)
        // Try plain insert as fallback
        await supabase.from('daily_completions').insert({
          user_id: session.user.id,
          day_number: parseInt(day),
          completed_at: new Date().toISOString(),
        })
      }
      
      await loadStats()
    } catch (e) { 
      console.error('Day complete error:', e)
      // Still update local state even if DB fails
      await loadStats()
    }
  }

  function handleNavigate(dest) {
    if (['day1', 'day2', 'day8', 'resume', 'progress', 'resources', 'journal'].includes(dest)) {
      setSubView(dest)
      setActiveTab('mysearch')
    } else if (dest === 'chat') {
      // "Build with AI Coach" for STAR stories — Day 28 already has this prompt built in
      setSubView('day28')
      setActiveTab('mysearch')
    } else {
      setActiveTab(dest)
      setSubView(null)
    }
  }

  function handleOpenCoach(prompt) {
    // No longer used - prompts are copyable instead
  }

  async function handleSignOut() {
    try { Object.keys(localStorage).forEach(k => { if (k.includes(session.user.id)) localStorage.removeItem(k) }) } catch {}
    await supabase.auth.signOut()
  }

  const progressPct = (currentDay / 60) * 100
  const dayLabel = currentDay <= 60 ? `Day ${currentDay}` : 'Today'

  // Pipeline health
  const getHealth = () => {
    if (stats.pipeline >= 5) return { color: '#00712D', bg: '#E8F2EC', label: 'Pipeline active' }
    if (stats.pipeline >= 3) return { color: '#00712D', bg: '#E8F2EC', label: 'Pipeline building' }
    if (stats.pipeline >= 1) return { color: '#555', bg: '#f5f5f5', label: 'Pipeline starting' }
    return { color: '#555', bg: '#f5f5f5', label: currentDay <= 8 ? 'Pipeline starts on Day 9' : 'Add your first opportunity' }
  }
  const health = getHealth()

  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', paddingBottom: '72px' }}>
      {pendingMilestone && <MilestonePopup milestone={pendingMilestone} onClose={clearMilestone} />}

      {/* Header */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0.75rem 1rem' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontFamily: 'Georgia,serif', fontSize: '0.9rem', fontWeight: 700, color: '#00712D' }}>The Application Multiplier</div>
            <div style={{ fontSize: '0.68rem', color: '#888', marginTop: '0.1rem' }}>{profile?.target_titles || 'Your 60-day search'}</div>
          </div>
          <button className="btn-ghost" onClick={handleSignOut} style={{ fontSize: '0.72rem' }}>Sign out</button>
        </div>
      </div>

      {/* Day arc */}
      <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0.85rem 1rem' }}>
        <div style={{ maxWidth: '700px', margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '0.75rem' }}>
            <div style={{ width: '58px', height: '58px', borderRadius: '50%', border: '3px solid #00712D', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', background: '#fff', flexShrink: 0 }}>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#00712D', lineHeight: 1, fontFamily: 'Georgia,serif' }}>{currentDay > 60 ? '✓' : `🔥${stats.activeDays}`}</div>
              {currentDay <= 60 && <div style={{ fontSize: '0.5rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>active days</div>}
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', gap: '0.85rem', marginBottom: '0.5rem', flexWrap: 'wrap' }}>
                {[
                  { l: 'Day', v: currentDay > 60 ? '60+' : currentDay },
                  { l: 'Pipeline', v: stats.pipeline },
                  { l: 'Hot Ten', v: `${stats.hotTen}/10` },
                  { l: 'Referrals', v: stats.referrals },
                ].map(s => (
                  <div key={s.l} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '1rem', fontWeight: 700, color: '#00712D', fontFamily: 'Georgia,serif', lineHeight: 1 }}>{s.v}</div>
                    <div style={{ fontSize: '0.58rem', color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{s.l}</div>
                  </div>
                ))}
              </div>
              <div style={{ height: '4px', background: '#e8e8e8', borderRadius: '2px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${Math.min(progressPct, 100)}%`, background: '#00712D', borderRadius: '2px' }} />
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.5rem 0.75rem', borderRadius: '6px', background: health.bg }}>
            <div style={{ width: '6px', height: '6px', borderRadius: '50%', background: health.color, flexShrink: 0 }} />
            <span style={{ fontSize: '0.72rem', fontWeight: 600, color: health.color }}>
              {health.label}{stats.pipeline > 0 ? ` — ${stats.pipeline} active ${stats.pipeline === 1 ? 'opportunity' : 'opportunities'}` : ''}
            </span>
          </div>
        </div>
      </div>

      {/* Content */}
      <div style={{ maxWidth: '700px', margin: '0 auto', padding: '1rem' }}>
        {activeTab === 'today' && (
          <>
            {daysSinceActive != null && daysSinceActive >= 3 && (
              <div className="card" style={{ background: '#FFFBF0', borderLeft: '4px solid #C9A84C', borderRadius: '0 10px 10px 0', marginBottom: '1rem' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: '#1A1A1A', marginBottom: '0.25rem' }}>Welcome back 👋</div>
                <p style={{ fontSize: '0.8rem', color: '#555', lineHeight: 1.6 }}>
                  It's been {daysSinceActive} days — life happens, and that's fine. Nothing here resets. Pick up right where you left off, whenever you're ready.
                </p>
              </div>
            )}
            <div style={{ marginBottom: '1rem' }}>
              <Forecast session={session} />
            </div>
            <DailyWorkbook
              session={session} profile={profile} currentDay={currentDay}
              activeDays={stats.activeDays} curriculum={CURRICULUM}
              onDayComplete={handleDayComplete} onStatsUpdate={loadStats}
            />
          </>
        )}
        {activeTab === 'mysearch' && !subView && (
          <MySearch session={session} profile={profile} onNavigate={handleNavigate} />
        )}
        {activeTab === 'pipeline' && <Pipeline session={session} onStatsUpdate={loadStats} />}
        {activeTab === 'network' && <HotTen session={session} onStatsUpdate={loadStats} />}
        {activeTab === 'resume' && <ResumeAnalyzer session={session} profile={profile} />}
        {activeTab === 'mysearch' && subView === 'resume' && <ResumeAnalyzer session={session} profile={profile} onBack={() => setSubView(null)} />}
        {activeTab === 'mysearch' && subView === 'progress' && <Progress session={session} profile={profile} currentDay={currentDay} activeDays={stats.activeDays} earnedMilestones={earnedMilestones} onBack={() => setSubView(null)} />}
        {activeTab === 'mysearch' && subView === 'journal' && <SearchJournal session={session} profile={profile} onBack={() => setSubView(null)} />}
        {activeTab === 'mysearch' && ['day1', 'day2', 'day8', 'day28'].includes(subView) && (
          <DailyWorkbook
            session={session} profile={profile} currentDay={currentDay}
            activeDays={stats.activeDays} curriculum={CURRICULUM}
            onDayComplete={handleDayComplete} onStatsUpdate={loadStats}
            initialDay={parseInt(subView.replace('day', ''), 10)}
            onBack={() => setSubView(null)}
          />
        )}
        {activeTab === 'mysearch' && subView === 'resources' && (
          <div>
            <button onClick={() => setSubView(null)} style={{ background: 'none', border: 'none', color: '#00712D', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: '0.85rem' }}>← Back to My Search</button>
            <div className="card" style={{ textAlign: 'center', padding: '2rem', color: '#888' }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📹</div>
              <div style={{ fontWeight: 600, marginBottom: '0.3rem', color: '#1A1A1A' }}>Video library coming soon</div>
              <p style={{ fontSize: '0.82rem' }}>This section isn't built yet — nothing to see here for now.</p>
            </div>
          </div>
        )}
      </div>

      {/* Bottom nav */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #e8e8e8', display: 'flex', zIndex: 50, paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {NAV.map(item => (
          <button key={item.id} onClick={() => { setActiveTab(item.id); setSubView(null) }} style={{ flex: 1, padding: '0.6rem 0.25rem 0.5rem', border: 'none', background: 'none', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.15rem', cursor: 'pointer', borderTop: activeTab === item.id ? '2px solid #00712D' : '2px solid transparent' }}>
            <span style={{ fontSize: '1.1rem' }}>{item.icon}</span>
            <span style={{ fontSize: '0.58rem', fontWeight: activeTab === item.id ? 700 : 400, color: activeTab === item.id ? '#00712D' : '#888' }}>{item.label}</span>
          </button>
        ))}
      </div>
    </div>
  )
}
